/**
 * Admin Reports API Client
 * Handles API calls for report generation endpoints.
 * Backend returns JSON data; PDF/Excel export is handled client-side
 * using jspdf and xlsx libraries.
 */

import axios from '@/lib/api/axios-instance';
import type {
  ReportDateRange,
  CustomReportRequest,
  ReportResponse,
  MonthlyRevenueReportData,
  CustomerGrowthReportData,
  SystemPerformanceReportData,
  VerificationSummaryRow,
  CreditBillingReportData,
  KycPipelineRow,
} from '@/src/types/admin/reports';

const BASE = '/api/admin/reports';

export async function generateMonthlyRevenueReport(
  range: ReportDateRange
): Promise<ReportResponse<MonthlyRevenueReportData>> {
  const res = await axios.post<ReportResponse<MonthlyRevenueReportData>>(
    `${BASE}/monthly-revenue`,
    range
  );
  return res.data;
}

export async function generateCustomerGrowthReport(
  range: ReportDateRange
): Promise<ReportResponse<CustomerGrowthReportData>> {
  const res = await axios.post<ReportResponse<CustomerGrowthReportData>>(
    `${BASE}/customer-growth`,
    range
  );
  return res.data;
}

export async function generateSystemPerformanceReport(
  range: ReportDateRange
): Promise<ReportResponse<SystemPerformanceReportData>> {
  const res = await axios.post<ReportResponse<SystemPerformanceReportData>>(
    `${BASE}/system-performance`,
    range
  );
  return res.data;
}

export async function generateVerificationSummaryReport(
  range: ReportDateRange
): Promise<ReportResponse<VerificationSummaryRow[]>> {
  const res = await axios.post<ReportResponse<VerificationSummaryRow[]>>(
    `${BASE}/verification-summary`,
    range
  );
  return res.data;
}

export async function generateCreditBillingReport(
  range: ReportDateRange
): Promise<ReportResponse<CreditBillingReportData>> {
  const res = await axios.post<ReportResponse<CreditBillingReportData>>(
    `${BASE}/credit-billing`,
    range
  );
  return res.data;
}

export async function generateKycPipelineReport(
  range: ReportDateRange
): Promise<ReportResponse<KycPipelineRow[]>> {
  const res = await axios.post<ReportResponse<KycPipelineRow[]>>(
    `${BASE}/kyc-pipeline`,
    range
  );
  return res.data;
}

export async function generateCustomReport(
  req: CustomReportRequest
): Promise<ReportResponse<Record<string, unknown>[]>> {
  const res = await axios.post<ReportResponse<Record<string, unknown>[]>>(
    `${BASE}/custom`,
    req
  );
  return res.data;
}
